/* eslint-disable @typescript-eslint/no-explicit-any */
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Button } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { DateRange } from '@mui/x-date-pickers-pro';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import {
  DateRangeQueryParams,
  EnhancedDateRangePicker
} from 'components/enhanced/form/adapters/date-time/EnhancedDateRangePicker';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import dayjs, { Dayjs } from 'dayjs';
import TenantPage from 'layouts/TenantPage';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConsumptionSettingsAccordion } from './components/ConsumptionSettingsAccordion';
import { UpdateThresholdsDialog } from './components/UpdateThresholdsDialog';
import toast from 'shared/utils/toast';
import { movementService } from '../movements/api/movement.service';
import { ConsumptionsColumns } from './consumptions';

/**
 * Calculates the default rolling date range for the initial page load.
 * Sets the start date to the first day of the same month from the previous year,
 * and the end date to the last day of the previous month.
 *
 * @returns {DateRange<Dayjs>} An array containing the start and end Dayjs objects.
 */
function buildDefaultDateRange(): DateRange<Dayjs> {
  const year = dayjs().year();
  const month = dayjs().month();
  const day = dayjs().subtract(1, 'month').daysInMonth();
  return [dayjs(`${year - 1}-${month + 1}-01`), dayjs(`${year}-${month}-${day}`)];
}

/**
 * Page component for Warehouse Consumption Analysis.
 *
 * Manages the visualization of historical product consumption trends via an EnhancedDataGrid,
 * handles chronological filtering through an advanced DateRangePicker, configures global tenant settings,
 * and orchestrates the automatic recalculation/bulk updating of safety stock thresholds
 * (understock, restock, overstock) for selected warehouse items.
 *
 * @component
 */
export const Consumption = () => {
  const { t } = useTranslation();
  const gridRefreshRef = useRef(null);
  const rowsRef = useRef(null);

  const [productWarehouses, setProductWarehouses] = useState<any[]>([]);
  const [openChangesDialog, setOpenChangesDialog] = useState(false);

  const defaultRange = buildDefaultDateRange();

  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(defaultRange);
  const [dateFilters, setDateFilters] = useState<DateRangeQueryParams>({
    timestamp__gte: defaultRange[0]?.format('YYYY-MM-DD') ?? '',
    timestamp__lte: defaultRange[1]?.format('YYYY-MM-DD') ?? ''
  });

  /**
   * Handles intermediate date variations during active calendar browsing.
   * Updates the visual state without triggering network requests to the server.
   *
   * @param {DateRange<Dayjs>} newRange - The newly chosen date range interval.
   */
  const handleDateChange = (newRange: DateRange<Dayjs>) => {
    setDateRange(newRange);
  };

  /**
   * Validates and applies the selected date range when the user explicitly clicks the "OK/Accept" action.
   * Converts the Dayjs objects into API-compliant strings and triggers a reactive table re-fetch.
   *
   * @param {DateRange<Dayjs>} acceptedRange - The confirmed date range interval.
   */
  const handleDateAccept = useCallback((acceptedRange: DateRange<Dayjs>) => {
    const [start, end] = acceptedRange;

    if (!start?.isValid() || !end?.isValid()) {
      toast.error('Inserire date di inizio e fine valide.');
      return;
    }

    setDateRange(acceptedRange);
    setDateFilters({
      timestamp__gte: start.format('YYYY-MM-DD'),
      timestamp__lte: end.format('YYYY-MM-DD')
    });
  }, []);

  /**
   * Captures row selection adjustments within the DataGrid.
   * Filters the master dataset utilizing the updated ID selection to sync selected record entities.
   *
   * @param {GridRowSelectionModel} selectionModel - Array containing the IDs of all currently checked rows.
   */
  const onSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
    const selectedRows = rowsRef.current.filter((row) => selectionModel.includes(row.id));
    setProductWarehouses(selectedRows);
  };

  /**
   * Callback utility invoked by downstream children or modals to request
   * an immediate UI data refresh of the parent grid.
   */
  const handleRefreshGrid = () => {
    if (gridRefreshRef.current) gridRefreshRef.current();
  };

  const columns: GridColDef[] = ConsumptionsColumns(dateRange[0], dateRange[1]);

  return (
    <TenantPage
      title={t('pages.consumption.title')}
      subtitle={t('pages.consumption.subtitle')}
      menuRight={
        <div className="flex items-center gap-2">
          <EnhancedDateRangePicker
            value={dateRange}
            onChange={handleDateChange}
            onAccept={handleDateAccept}
          />
          <IsTenantAdministrator>
            <Button
              variant="contained"
              onClick={() => setOpenChangesDialog(true)}
              disabled={productWarehouses.length === 0}>
              <AutoFixHighIcon />
              {t(`actions.update-thresholds`)}
            </Button>
          </IsTenantAdministrator>
        </div>
      }>
      <ConsumptionSettingsAccordion />
      <EnhancedDataGrid
        initialOptions={{
          pinnedColumns: {
            left: ['product', 'actions']
          }
        }}
        outerParams={dateFilters}
        service={movementService}
        refresh={gridRefreshRef}
        getMethod={movementService.consumptionDetail}
        columns={columns}
        rows={rowsRef}
        checkboxSelection={true}
        selectionModelChange={onSelectionModelChange}
      />
      <UpdateThresholdsDialog
        open={openChangesDialog}
        onClose={() => setOpenChangesDialog(false)}
        selectedRows={productWarehouses}
        onSuccess={handleRefreshGrid}
      />
    </TenantPage>
  );
};
